import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Shared constant for tooltip width - used for both positioning calculations and rendering
const TOOLTIP_MAX_WIDTH = 300;

/**
 * Custom hook for portal-based tooltips that escape overflow containers
 */
export const usePortalTooltip = ({ content, description, place = 'right', delayShow = 500, containerRef = null }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const timeoutRef = useRef(null);
	const elementRef = useRef(null);

	// Cleanup timeout on unmount to prevent state updates after unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, []);

	const showTooltip = (event) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		
		// Capture the element reference immediately when the event fires
		const targetElement = (event && event.currentTarget) || elementRef.current;
		if (!targetElement) return;
		
		timeoutRef.current = setTimeout(() => {
			if (!content) return;
			
			// Verify element still exists and is in the DOM
			if (!targetElement || !targetElement.getBoundingClientRect) {
				return;
			}
			
			try {
				const rect = targetElement.getBoundingClientRect();
				if (!rect) return;
				
				const tooltipWidth = TOOLTIP_MAX_WIDTH; // Use shared constant for consistent positioning
				const tooltipHeight = description ? 80 : 40; // Estimated tooltip height
				const spacing = 10; // Space between element and tooltip
				
				// Get container rect if containerRef is provided
				let containerRect = null;
				if (containerRef && containerRef.current) {
					try {
						const containerElement = containerRef.current;
						if (containerElement && containerElement.getBoundingClientRect) {
							containerRect = containerElement.getBoundingClientRect();
							// Validate that we got a valid rect with a valid left position
							if (!containerRect || containerRect.width === 0 || containerRect.left === undefined || isNaN(containerRect.left)) {
								containerRect = null;
							}
						}
					} catch (e) {
						// Ignore errors getting container rect
						containerRect = null;
					}
				}
				
				let x = 0;
				let y = 0;
				let useFixedX = false; // Flag to indicate if we should use a fixed x position
				
				// Calculate position based on placement
				switch (place) {
					case 'right':
						x = rect.right + spacing;
						y = rect.top + (rect.height / 2) - (tooltipHeight / 2);
						break;
					case 'left':
						// Use container's left edge if containerRef is provided, otherwise use element's left edge
						if (containerRect && containerRect.left !== undefined && !isNaN(containerRect.left)) {
							// Position tooltip so its right edge aligns with container's left edge minus spacing
							// This ensures all tooltips align regardless of their actual width
							// We'll position the left edge, but the right edge will be at: containerRect.left - spacing
							x = containerRect.left - tooltipWidth - spacing;
							useFixedX = true; // Mark that we're using a fixed x position
						} else {
							// Fallback to element's left edge if container not available
							x = rect.left - tooltipWidth - spacing;
						}
						y = rect.top + (rect.height / 2) - (tooltipHeight / 2);
						break;
					case 'top':
						x = rect.left + (rect.width / 2) - (tooltipWidth / 2);
						y = rect.top - tooltipHeight - spacing;
						break;
					case 'bottom':
						x = rect.left + (rect.width / 2) - (tooltipWidth / 2);
						y = rect.bottom + spacing;
						break;
					default:
						// Default to right placement
						x = rect.right + spacing;
						y = rect.top + (rect.height / 2) - (tooltipHeight / 2);
				}
				
				// Clamp to viewport (but preserve fixed x position when using containerRef)
				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;
				const padding = 10;
				
				// Only clamp x if we're not using a fixed x position from container
				if (!useFixedX) {
					if (x + tooltipWidth > viewportWidth - padding) {
						x = viewportWidth - tooltipWidth - padding;
					}
					if (x < padding) x = padding;
				}
				
				if (y + tooltipHeight > viewportHeight - padding) {
					y = viewportHeight - tooltipHeight - padding;
				}
				if (y < padding) y = padding;
				
				setPosition({ x, y });
				setIsVisible(true);
			} catch (error) {
				console.warn('Error calculating tooltip position:', error);
			}
		}, delayShow);
	};

	const hideTooltip = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setIsVisible(false);
	};

	const tooltipProps = {
		ref: elementRef,
		onMouseEnter: showTooltip,
		onMouseLeave: hideTooltip,
		onFocus: showTooltip,
		onBlur: hideTooltip,
	};

	const TooltipPortal = () => {
		if (!isVisible || !content) return null;

		return createPortal(
			<div
				className="fixed z-[99999] pointer-events-none p-4 rounded-lg bg-surface border border-border max-w-[300px] shadow-[0_4px_12px_rgba(0,0,0,0.15)]"
				style={{
					left: `${position.x}px`,
					top: `${position.y}px`,
				}}
			>
				{description ? (
					<>
						<div className="text-base font-bold mb-2 text-text-2">{content}</div>
						<div className="text-sm text-text-3">{description}</div>
					</>
				) : (
					<div className="font-bold text-text-3">{content}</div>
				)}
			</div>,
			document.body
		);
	};

	return { tooltipProps, TooltipPortal };
};
